import { Badge, Button, Card, cn } from '@design-system';
import { WaterlilyLogo } from '@/components/WaterlilyLogo';
import { BookACallButton } from './book-a-call-button';
import { CaseDetailsField } from './case-details-field';

import { Check, ClipboardCheck, MessagesSquare, Sparkles, Wand2, X } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { possessive,
  getFreemiumCheckoutFeatures,
  getFreemiumTierMeta,
  resolveFreemiumTier,
} from '@shared';

import { selectClient } from '@/model';
import { useFreemiumUrlState } from '../hooks/use-freemium-url-state';
import { freemiumIntentToolPath } from '../lib/intent-routing';

interface FreemiumPaidSuccessProps {
  clientId: string;
  clientFirstName: string;
  onContinue: () => void;
}

const SERVICE_STEPS = [
  {
    icon: <ClipboardCheck className="size-5" />,
    title: 'We review your case',
    description: 'Our licensed team digs into the details you share with us.',
  },
  {
    icon: <Wand2 className="size-5" />,
    title: 'We build your materials',
    description: 'A tailored report, talking points, and an email-ready summary.',
  },
  {
    icon: <MessagesSquare className="size-5" />,
    title: "We're here for you",
    description: 'Reach out any time, or book a call to walk through everything.',
  },
];

export function FreemiumPaidSuccess({
  clientId,
  clientFirstName,
  onContinue,
}: FreemiumPaidSuccessProps) {
  const navigate = useNavigate();
  const client = useSelector(selectClient);
  const { intent, reflexiveAnswer, selectedTier } = useFreemiumUrlState();

  function handleClose() {
    if (client?.advisorId) {
      navigate(`/advisors/${client.advisorId}/clients`);
    } else {
      navigate(-1);
    }
  }

  // Continue should drop the advisor on the action for the intent they came in
  // with. Self-funding and consult intents have no tool path — they stay where
  // the success screen sits (guided planning / funding-sources).
  function handleContinue() {
    const toolPath = freemiumIntentToolPath(clientId, intent);
    onContinue();
    if (toolPath) navigate(toolPath);
  }

  const tier = selectedTier;
  const isService = tier === 'licensedFullService';
  const tierName = tier ? getFreemiumTierMeta(tier).name : 'your plan';
  const config = intent && tier ? resolveFreemiumTier(intent, reflexiveAnswer, tier) : undefined;
  const features =
    intent && tier ? getFreemiumCheckoutFeatures(intent, reflexiveAnswer, tier) : config?.features;

  return (
    <div className="bg-background fixed inset-0 z-50 flex flex-col">
      <header className="flex shrink-0 items-center justify-between border-b p-4 md:px-6">
        <WaterlilyLogo className="h-7 w-auto" />
        <Button
          type="button"
          aria-label="Close"
          onClick={handleClose}
          variant="outline"
          size="icon-sm"
          className="rounded-full"
        >
          <X />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex min-h-[calc(100vh-70px)] w-full max-w-lg flex-col justify-center px-4 py-10 md:px-6">
          <div className="flex flex-col items-center text-center">
            <Badge>
              <Check />
              Payment confirmed
            </Badge>
            <h1 className="mt-4 text-balance text-3xl font-bold text-gray-950">
              {isService ? "We're on it" : "You're all set"}
            </h1>
            <p className="text-foreground mt-2 text-balance">
              {isService
                ? `Our licensed team will review ${possessive(clientFirstName)} case and prepare your full plan and materials. We'll get to work right away — meanwhile, keep exploring Waterlily.`
                : `Your ${tierName} access is unlocked. Dive in and start building ${possessive(clientFirstName)} plan.`}
            </p>
          </div>

          {isService ? (
            <Card className="mt-6 flex flex-col gap-5 p-6 shadow-none">
              {SERVICE_STEPS.map(step => (
                <div
                  key={step.title}
                  className="flex items-start gap-4 text-start"
                >
                  <span className="bg-primary/10 text-primary flex size-11 shrink-0 items-center justify-center rounded-md">
                    {step.icon}
                  </span>
                  <div>
                    <p className="font-medium text-gray-950">{step.title}</p>
                    <p className="text-foreground text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </Card>
          ) : (
            features &&
            features.length > 0 && (
              <Card className="mt-6 p-6 shadow-none">
                <p className="mb-3 flex items-center gap-2 font-medium text-gray-950">
                  <Sparkles className="text-primary size-4" />
                  What you can do now
                </p>
                <ul className="space-y-2.5">
                  {features.map(feature => (
                    <li
                      key={feature}
                      className="flex items-start gap-2 text-sm"
                    >
                      <Check className="text-primary mt-0.5 size-4 shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )
          )}

          {isService && (
            <div className="mt-6">
              <CaseDetailsField
                clientId={clientId}
                clientFirstName={clientFirstName}
              />
            </div>
          )}

          <Button
            size="lg"
            onClick={handleContinue}
            className={cn('w-full', isService ? 'mt-6' : 'mt-8')}
          >
            {isService ? 'Explore Waterlily while we work' : 'Start exploring'}
          </Button>
          <BookACallButton className="mt-3" />
        </div>
      </div>
    </div>
  );
}
